import {
    Alert,
    AlertIcon,
    Badge,
    Box,
    Button,
    ButtonGroup,
    Flex,
    HStack,
    Link,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    SimpleGrid,
    Skeleton,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
} from "@chakra-ui/react";
import { CopyIcon, ExternalLinkIcon, InfoOutlineIcon } from "@chakra-ui/icons";
import toast from "react-hot-toast";
import { useEffect, useRef, useState } from "react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip as ChartTooltip,
    XAxis,
    YAxis,
} from "recharts";
import { Pagination } from "./components/Pagination";
import {
    fetchRetention,
    fetchRetentionWallets,
    RetentionData,
    RetentionPlatform,
    RetentionStatus,
    RetentionWalletsData,
    RetentionWeek,
} from "./retentionApi";

const PLATFORMS: { key: RetentionPlatform; label: string }[] = [
    { key: "all", label: "All" },
    { key: "gmx", label: "GMX" },
    { key: "hyperliquid", label: "Hyperliquid" },
];
const RANGES = [8, 12, 26];

const STATUS_COLORS: Record<RetentionStatus, string> = {
    new: "#4299E1",
    retained: "#48BB78",
    returning: "#9F7AEA",
    churned: "#F56565",
};
const STATUS_LABELS: Record<RetentionStatus, string> = {
    new: "New",
    retained: "Retained",
    returning: "Returning",
    churned: "Churned",
};

// Known limitations shown in the UI (backend plan §10, entries marked "UI")
const LIMITATIONS = {
    counting:
        "Counts GMX position orders (no swaps or liquidations) and Hyperliquid fills. Cancelled GMX market orders still count, as in the stats above and the leaderboard. Hyperliquid liquidation fills can't be told apart yet. GMX trades are tracked only for custody wallets.",
    active:
        "Differs from Active Wallets above: that figure uses order creation time, includes swaps, doesn't merge custody wallets into their owner and has no exclusions.",
    dataAsOf: "Each refresh scans all orders and fills; results are cached for up to 60 seconds.",
    hlOwnWallet:
        "Hyperliquid fills synced from a user's own HL wallet are counted and may include trades made outside PerpX.",
    baseline:
        "Data collection starts here, so this week's new traders include people who traded before it began.",
    drillVolume:
        "For limit and trigger orders this is the size at placement; the leaderboard uses the size that filled.",
};

// ── helpers ──────────────────────────────────────────────────────────────────

const pct = (rate: number | null | undefined) =>
    rate == null ? "—" : `${(rate * 100).toFixed(1)}%`;

const weekLabel = (weekStart: string) =>
    new Date(`${weekStart}T00:00:00Z`).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
    });

const fmtUsd = (n: number) =>
    new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
    }).format(n);

const shortAddr = (addr: string) => `${addr.slice(0, 6)}…${addr.slice(-4)}`;

// No user detail page exists yet; link to the venue's explorer meanwhile
const explorerUrl = (address: string, platform: RetentionPlatform) =>
    platform === "hyperliquid"
        ? `https://app.hyperliquid.xyz/explorer/address/${address}`
        : `https://arbiscan.io/address/${address}`;

const InfoTip = ({ label }: { label: string }) => (
    <Tooltip label={label} hasArrow placement="top" maxW="320px">
        <InfoOutlineIcon ml={1} boxSize={3} color="gray.500" cursor="help" />
    </Tooltip>
);

const panelProps = {
    bg: "gray.800",
    border: "1px solid",
    borderColor: "gray.700",
    borderRadius: "12px",
    p: 5,
} as const;

// ── KPI tile ─────────────────────────────────────────────────────────────────

interface KpiProps {
    label: string;
    value: string;
    change?: string | null;
    color: string;
    tip?: string;
    onClick?: () => void;
}

function Kpi({ label, value, change, color, tip, onClick }: KpiProps) {
    return (
        <Box
            {...panelProps}
            position="relative"
            overflow="hidden"
            cursor={onClick ? "pointer" : undefined}
            onClick={onClick}
            _hover={onClick ? { borderColor: "gray.600" } : undefined}
        >
            <Box position="absolute" top={0} left={0} right={0} height="2px" bg={color} />
            <Flex align="center" mb={2}>
                <Text fontSize="sm" fontWeight="500" color="gray.400" textTransform="uppercase" letterSpacing="wide">
                    {label}
                </Text>
                {tip && <InfoTip label={tip} />}
            </Flex>
            <Text fontSize="2xl" fontWeight="700" color="white" lineHeight={1}>
                {value}
            </Text>
            {change && (
                <Text fontSize="xs" color="gray.500" mt={1}>
                    {change} vs previous week
                </Text>
            )}
        </Box>
    );
}

const signed = (n: number, suffix = "") => `${n > 0 ? "+" : ""}${n}${suffix}`;

// ── Drill-down ───────────────────────────────────────────────────────────────

interface DrillTarget {
    weekStart: string;
    status: RetentionStatus;
}

function DrillDown({
    target,
    platform,
    onClose,
}: {
    target: DrillTarget | null;
    platform: RetentionPlatform;
    onClose: () => void;
}) {
    const [status, setStatus] = useState<RetentionStatus>("new");
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [data, setData] = useState<RetentionWalletsData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const requestId = useRef(0);

    useEffect(() => {
        if (!target) return;
        setStatus(target.status);
        setPage(1);
        setData(null);
    }, [target]);

    useEffect(() => {
        if (!target) return;
        const id = ++requestId.current;
        setLoading(true);
        setError("");
        fetchRetentionWallets({ weekStart: target.weekStart, status, platform, page, limit })
            .then((d) => {
                if (id === requestId.current) setData(d);
            })
            .catch((err) => {
                if (id === requestId.current) {
                    setError(err.response?.data?.message || err.message || "Error loading wallets");
                }
            })
            .finally(() => {
                if (id === requestId.current) setLoading(false);
            });
    }, [target, status, platform, page, limit]);

    const copy = (text: string) => {
        navigator.clipboard.writeText(text);
        toast.success("Copied!");
    };

    return (
        <Modal isOpen={!!target} onClose={onClose} size="6xl" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent bg="gray.900" color="white">
                <ModalHeader>
                    Week of {target ? weekLabel(target.weekStart) : ""}
                    {data && (
                        <Text as="span" fontSize="sm" color="gray.400" fontWeight="400" ml={2}>
                            {data.weekStart} – {data.weekEnd} UTC
                        </Text>
                    )}
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6}>
                    <ButtonGroup size="sm" isAttached variant="outline" mb={4}>
                        {(Object.keys(STATUS_LABELS) as RetentionStatus[]).map((s) => (
                            <Button
                                key={s}
                                variant={status === s ? "solid" : "outline"}
                                colorScheme={status === s ? "blue" : "gray"}
                                onClick={() => {
                                    setStatus(s);
                                    setPage(1);
                                }}
                            >
                                {STATUS_LABELS[s]}
                            </Button>
                        ))}
                    </ButtonGroup>

                    {data && !data.isComplete && (
                        <Alert status="warning" mb={4} borderRadius="md">
                            <AlertIcon />
                            Week in progress — statuses are provisional
                        </Alert>
                    )}
                    {error && (
                        <Alert status="error" mb={4} borderRadius="md">
                            <AlertIcon />
                            {error}
                        </Alert>
                    )}

                    {data && (
                        <Text fontSize="sm" color="gray.400" mb={2}>
                            {data.total.toLocaleString()} {STATUS_LABELS[status].toLowerCase()} wallets
                        </Text>
                    )}

                    <Box overflowX="auto" opacity={loading ? 0.5 : 1}>
                        <Table size="sm" variant="simple">
                            <Thead>
                                <Tr>
                                    <Th color="gray.400">Address</Th>
                                    <Th color="gray.400">First trade week</Th>
                                    <Th color="gray.400" isNumeric>
                                        Trades this / prev week
                                    </Th>
                                    <Th color="gray.400" isNumeric>
                                        Volume this / prev week
                                        <InfoTip label={LIMITATIONS.drillVolume} />
                                    </Th>
                                    <Th color="gray.400">Last trade week</Th>
                                    <Th color="gray.400">Next trade week</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {loading && !data
                                    ? Array.from({ length: 5 }).map((_, i) => (
                                          <Tr key={i}>
                                              <Td colSpan={6}>
                                                  <Skeleton height="16px" />
                                              </Td>
                                          </Tr>
                                      ))
                                    : data?.wallets.map((w) => (
                                          <Tr key={w.address}>
                                              <Td fontFamily="mono">
                                                  <Link
                                                      href={explorerUrl(w.address, platform)}
                                                      isExternal
                                                      color="blue.300"
                                                  >
                                                      {shortAddr(w.address)} <ExternalLinkIcon mx="2px" />
                                                  </Link>
                                                  <CopyIcon ml={1} cursor="pointer" onClick={() => copy(w.address)} />
                                              </Td>
                                              <Td>{w.firstTradeWeek}</Td>
                                              <Td isNumeric>
                                                  {w.thisWeek.trades} / {w.prevWeek.trades}
                                              </Td>
                                              <Td isNumeric>
                                                  {fmtUsd(w.thisWeek.volume)} / {fmtUsd(w.prevWeek.volume)}
                                              </Td>
                                              <Td>{w.lastTradeWeek ?? "—"}</Td>
                                              <Td>{w.nextTradeWeek ?? "—"}</Td>
                                          </Tr>
                                      ))}
                                {data && data.wallets.length === 0 && (
                                    <Tr>
                                        <Td colSpan={6} textAlign="center" color="gray.500">
                                            No wallets
                                        </Td>
                                    </Tr>
                                )}
                            </Tbody>
                        </Table>
                    </Box>
                    {status === "churned" && (
                        <Text fontSize="xs" color="gray.500" mt={2}>
                            Churned wallets traded in the previous week and not in this one.
                        </Text>
                    )}

                    {data && data.total > 0 && (
                        <Pagination
                            currentPage={page}
                            totalPages={Math.max(1, Math.ceil(data.total / limit))}
                            itemsPerPage={limit}
                            totalItems={data.total}
                            onPageChange={setPage}
                            onItemsPerPageChange={(n) => {
                                setLimit(n);
                                setPage(1);
                            }}
                        />
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}

// ── Charts ───────────────────────────────────────────────────────────────────

interface ChartPoint extends RetentionWeek {
    label: string;
    rate: number | null;
    rateSoFar: number | null;
    churnedNeg: number;
}

function toChartPoints(weeks: RetentionWeek[]): ChartPoint[] {
    const inProgress = weeks.findIndex((w) => !w.isComplete);
    return weeks.map((w, i) => {
        const pctRate = w.retentionRate == null ? null : w.retentionRate * 100;
        return {
            ...w,
            label: weekLabel(w.weekStart),
            rate: w.isComplete ? pctRate : null,
            // Dashed segment joining the last complete week to the in-progress one
            rateSoFar: inProgress >= 0 && (i === inProgress || i === inProgress - 1) ? pctRate : null,
            churnedNeg: -w.churnedTraders,
        };
    });
}

const tooltipStyle = {
    contentStyle: { background: "#1A202C", border: "1px solid #4A5568", borderRadius: 8 },
    labelStyle: { color: "#E2E8F0" },
};

function RetentionLine({ points }: { points: ChartPoint[] }) {
    return (
        <ResponsiveContainer width="100%" height={260}>
            <LineChart data={points} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#2D3748" strokeDasharray="3 3" />
                <XAxis dataKey="label" stroke="#A0AEC0" fontSize={12} />
                <YAxis stroke="#A0AEC0" fontSize={12} domain={[0, 100]} unit="%" />
                <ChartTooltip
                    {...tooltipStyle}
                    formatter={(value: number, name, item) => {
                        const p = item.payload as ChartPoint;
                        if (name === "rateSoFar" && p.isComplete) return [null, null];
                        return [
                            `${value.toFixed(1)}%${p.isComplete ? "" : " so far (week in progress)"}`,
                            "Retention",
                        ];
                    }}
                />
                <Line
                    type="monotone"
                    dataKey="rate"
                    stroke="#48BB78"
                    strokeWidth={2}
                    connectNulls={false}
                    dot={{ r: 3 }}
                    isAnimationActive={false}
                />
                <Line
                    type="monotone"
                    dataKey="rateSoFar"
                    stroke="#718096"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    connectNulls={false}
                    dot={{ r: 3, fill: "#718096" }}
                    legendType="none"
                    isAnimationActive={false}
                />
            </LineChart>
        </ResponsiveContainer>
    );
}

function StatusBars({
    points,
    onSelect,
}: {
    points: ChartPoint[];
    onSelect: (weekStart: string, status: RetentionStatus) => void;
}) {
    const click = (status: RetentionStatus) => (bar: any) => {
        if (bar?.payload?.weekStart) onSelect(bar.payload.weekStart, status);
    };
    return (
        <ResponsiveContainer width="100%" height={300}>
            <BarChart data={points} stackOffset="sign" margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <defs>
                    <pattern id="churn-hatch" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
                        <rect width="6" height="6" fill="#F5656533" />
                        <line x1="0" y1="0" x2="0" y2="6" stroke="#F56565" strokeWidth="2" />
                    </pattern>
                </defs>
                <CartesianGrid stroke="#2D3748" strokeDasharray="3 3" />
                <XAxis dataKey="label" stroke="#A0AEC0" fontSize={12} />
                <YAxis stroke="#A0AEC0" fontSize={12} tickFormatter={(v) => String(Math.abs(v))} />
                <ReferenceLine y={0} stroke="#718096" />
                <ChartTooltip
                    {...tooltipStyle}
                    cursor={{ fill: "#2D374855" }}
                    formatter={(value: number, name, item) => {
                        const p = item.payload as ChartPoint;
                        const label =
                            name === "Churned" && !p.isComplete ? "Churned (not back yet)" : name;
                        return [Math.abs(value), label];
                    }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="newTraders" name="New" stackId="s" fill={STATUS_COLORS.new} isAnimationActive={false} cursor="pointer" onClick={click("new")} />
                <Bar dataKey="retainedTraders" name="Retained" stackId="s" fill={STATUS_COLORS.retained} isAnimationActive={false} cursor="pointer" onClick={click("retained")} />
                <Bar dataKey="returningTraders" name="Returning" stackId="s" fill={STATUS_COLORS.returning} isAnimationActive={false} cursor="pointer" onClick={click("returning")} />
                <Bar dataKey="churnedNeg" name="Churned" stackId="s" fill={STATUS_COLORS.churned} isAnimationActive={false} cursor="pointer" onClick={click("churned")}>
                    {points.map((p) => (
                        <Cell key={p.weekStart} fill={p.isComplete ? STATUS_COLORS.churned : "url(#churn-hatch)"} />
                    ))}
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
}

function CohortHeatmap({
    data,
    onSelect,
}: {
    data: RetentionData;
    onSelect: (weekStart: string) => void;
}) {
    const maxOffset = Math.max(0, ...data.cohorts.map((c) => c.retention.length));
    const offsets = Array.from({ length: maxOffset }, (_, i) => i + 1);
    return (
        <Box overflowX="auto">
            <Table size="sm" variant="unstyled">
                <Thead>
                    <Tr>
                        <Th color="gray.400">Cohort</Th>
                        <Th color="gray.400" isNumeric>
                            Size
                        </Th>
                        {offsets.map((k) => (
                            <Th key={k} color="gray.400" textAlign="center">
                                +{k}w
                            </Th>
                        ))}
                    </Tr>
                </Thead>
                <Tbody>
                    {data.cohorts.map((c) => {
                        const cells = new Map(c.retention.map((r) => [r.weekOffset, r]));
                        return (
                            <Tr
                                key={c.cohortWeekStart}
                                cursor="pointer"
                                _hover={{ bg: "gray.700" }}
                                onClick={() => onSelect(c.cohortWeekStart)}
                            >
                                <Td whiteSpace="nowrap">
                                    {weekLabel(c.cohortWeekStart)}
                                    {c.isBaseline && (
                                        <Tooltip label={LIMITATIONS.baseline} hasArrow>
                                            <Badge ml={2} colorScheme="yellow" fontSize="0.6rem">
                                                includes pre-existing traders
                                            </Badge>
                                        </Tooltip>
                                    )}
                                </Td>
                                <Td isNumeric>{c.size}</Td>
                                {offsets.map((k) => {
                                    const cell = cells.get(k);
                                    // Absent cells are future weeks: blank, not 0%
                                    if (!cell) return <Td key={k} />;
                                    return (
                                        <Td key={k} p={1}>
                                            <Tooltip label={`${cell.retained} of ${c.size} traded in week +${k}`} hasArrow>
                                                <Box
                                                    textAlign="center"
                                                    borderRadius="4px"
                                                    py={1}
                                                    fontSize="xs"
                                                    bg={
                                                        cell.rate == null
                                                            ? "transparent"
                                                            : `rgba(72, 187, 120, ${0.1 + cell.rate * 0.8})`
                                                    }
                                                    color={cell.rate != null && cell.rate > 0.5 ? "gray.900" : "white"}
                                                >
                                                    {pct(cell.rate)}
                                                </Box>
                                            </Tooltip>
                                        </Td>
                                    );
                                })}
                            </Tr>
                        );
                    })}
                </Tbody>
            </Table>
        </Box>
    );
}

// ── Section ──────────────────────────────────────────────────────────────────

function TraderRetention() {
    const [platform, setPlatform] = useState<RetentionPlatform>("all");
    const [weeks, setWeeks] = useState(12);
    const [data, setData] = useState<RetentionData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [drill, setDrill] = useState<DrillTarget | null>(null);
    // Ignore responses for a venue / range the user has already moved away from
    const requestId = useRef(0);

    useEffect(() => {
        const id = ++requestId.current;
        setLoading(true);
        setError("");
        fetchRetention({ weeks, platform })
            .then((d) => {
                if (id === requestId.current) setData(d);
            })
            .catch((err) => {
                if (id === requestId.current) {
                    setError(err.response?.data?.message || err.message || "Error loading trader retention");
                }
            })
            .finally(() => {
                if (id === requestId.current) setLoading(false);
            });
    }, [weeks, platform]);

    const last = data?.summary.lastCompleteWeek ?? null;
    const prev = data?.summary.previousCompleteWeek ?? null;
    const points = data ? toChartPoints(data.weeks) : [];
    const open = (weekStart: string, status: RetentionStatus) => setDrill({ weekStart, status });

    const rateChange =
        last?.retentionRate != null && prev?.retentionRate != null
            ? signed(Math.round((last.retentionRate - prev.retentionRate) * 1000) / 10, " pp")
            : null;
    const countChange = (key: "activeTraders" | "newTraders" | "churnedTraders") =>
        last && prev ? signed(last[key] - prev[key]) : null;

    return (
        <Box mt={10}>
            <Flex justify="space-between" align={{ base: "start", md: "center" }} wrap="wrap" gap={3} mb={5}>
                <Box>
                    <Flex align="center">
                        <Text fontSize="2xl" fontWeight="700" color="white">
                            Trader Retention
                        </Text>
                        <InfoTip label={LIMITATIONS.counting} />
                    </Flex>
                    <Text color="gray.400" fontSize="sm">
                        Share of last week's traders who traded again this week
                    </Text>
                </Box>
                <HStack spacing={3} wrap="wrap">
                    <ButtonGroup size="sm" isAttached>
                        {PLATFORMS.map(({ key, label }) => (
                            <Button
                                key={key}
                                variant={platform === key ? "solid" : "outline"}
                                colorScheme={platform === key ? "blue" : "gray"}
                                onClick={() => setPlatform(key)}
                            >
                                {label}
                            </Button>
                        ))}
                    </ButtonGroup>
                    {platform !== "gmx" && <InfoTip label={LIMITATIONS.hlOwnWallet} />}
                    <ButtonGroup size="sm" isAttached>
                        {RANGES.map((n) => (
                            <Button
                                key={n}
                                variant={weeks === n ? "solid" : "outline"}
                                colorScheme={weeks === n ? "blue" : "gray"}
                                onClick={() => setWeeks(n)}
                            >
                                {n}w
                            </Button>
                        ))}
                    </ButtonGroup>
                </HStack>
            </Flex>

            {data && (
                <Flex align="center" mb={4}>
                    <Text fontSize="xs" color="gray.500">
                        Data as of {new Date(data.dataAsOf).toLocaleString()}
                    </Text>
                    <InfoTip label={LIMITATIONS.dataAsOf} />
                </Flex>
            )}

            {error && (
                <Alert status="error" mb={6}>
                    <AlertIcon />
                    {error}
                </Alert>
            )}

            {loading && !data ? (
                <>
                    <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={4} mb={6}>
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Skeleton key={i} height="96px" borderRadius="12px" />
                        ))}
                    </SimpleGrid>
                    <Skeleton height="260px" borderRadius="12px" mb={6} />
                    <Skeleton height="300px" borderRadius="12px" />
                </>
            ) : data && data.weeks.length === 0 ? (
                <Box {...panelProps} textAlign="center" color="gray.400" py={10}>
                    No trading data yet
                </Box>
            ) : data ? (
                <Box opacity={loading ? 0.5 : 1} transition="opacity 0.2s">
                    {last && (
                        <Flex align="center" mb={2} gap={2}>
                            <Text fontSize="sm" color="gray.400">
                                Week of {weekLabel(last.weekStart)}
                            </Text>
                            {last.isBaseline && (
                                <Tooltip label={LIMITATIONS.baseline} hasArrow>
                                    <Badge colorScheme="yellow">Baseline week</Badge>
                                </Tooltip>
                            )}
                        </Flex>
                    )}
                    <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={4} mb={6}>
                        <Kpi
                            label="Retention"
                            value={pct(last?.retentionRate)}
                            change={rateChange}
                            color="green.400"
                            onClick={last ? () => open(last.weekStart, "retained") : undefined}
                        />
                        <Kpi
                            label="Active traders"
                            value={last ? last.activeTraders.toLocaleString() : "—"}
                            change={countChange("activeTraders")}
                            color="blue.400"
                            tip={LIMITATIONS.active}
                            onClick={last ? () => open(last.weekStart, "retained") : undefined}
                        />
                        <Kpi
                            label="New traders"
                            value={last ? last.newTraders.toLocaleString() : "—"}
                            change={countChange("newTraders")}
                            color="cyan.400"
                            onClick={last ? () => open(last.weekStart, "new") : undefined}
                        />
                        <Kpi
                            label="Churned traders"
                            value={last ? last.churnedTraders.toLocaleString() : "—"}
                            change={countChange("churnedTraders")}
                            color="red.400"
                            onClick={last ? () => open(last.weekStart, "churned") : undefined}
                        />
                    </SimpleGrid>

                    <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6} mb={6}>
                        <Box {...panelProps}>
                            <Text fontWeight="600" color="white" mb={3}>
                                Week-over-week retention
                            </Text>
                            <RetentionLine points={points} />
                        </Box>
                        <Box {...panelProps}>
                            <Text fontWeight="600" color="white" mb={3}>
                                Traders by status
                                <Text as="span" fontSize="xs" color="gray.500" fontWeight="400" ml={2}>
                                    click a bar for wallets
                                </Text>
                            </Text>
                            <StatusBars points={points} onSelect={open} />
                        </Box>
                    </SimpleGrid>

                    <Box {...panelProps} mb={4}>
                        <Text fontWeight="600" color="white" mb={1}>
                            New-trader cohorts
                        </Text>
                        <Text fontSize="xs" color="gray.500" mb={3}>
                            Share of each week's new traders who traded again exactly N weeks later. Click a row for its wallets.
                        </Text>
                        {data.cohorts.length === 0 ? (
                            <Text fontSize="sm" color="gray.500">
                                No complete weeks yet
                            </Text>
                        ) : (
                            <CohortHeatmap data={data} onSelect={(w) => open(w, "new")} />
                        )}
                    </Box>

                    <Text fontSize="xs" color="gray.500">
                        Weeks are Mon–Sun UTC. Data since{" "}
                        {data.dataSince ? new Date(data.dataSince).toISOString().slice(0, 10) : "—"}. Excludes swaps
                        and liquidations.
                    </Text>
                </Box>
            ) : null}

            <DrillDown target={drill} platform={platform} onClose={() => setDrill(null)} />
        </Box>
    );
}

export default TraderRetention;
